import { type ReactNode } from 'react'

import type { ClinicSession } from '../../api/specialists'
import { sessionLabel } from '../../lib/sessionLabel'
import { Icon } from '../Icon'

/**
 * Renders a specialist's recurring weekly clinic sessions at one hospital
 * (M14-T04). The API already orders them Mon→Sun then morning→afternoon→evening;
 * we render as human labels via the shared `sessionLabel` formatter. Falls back
 * to `availabilityNote` (or nothing) when there are no structured sessions.
 */
export function ClinicSessions({
  sessions,
  availabilityNote,
}: {
  sessions: ClinicSession[] | undefined
  availabilityNote?: string | null
}): ReactNode {
  if (sessions && sessions.length > 0) {
    return (
      <ul className="mt-1.5 flex flex-col gap-1">
        {sessions.map((s, i) => (
          <li
            key={`${s.day_of_week}-${s.part_of_day}-${i}`}
            className="inline-flex items-center gap-1.5 text-[12px] text-ink-2"
          >
            <Icon name="clock" size={12} className="shrink-0 text-ink-3" />
            {sessionLabel(s)}
          </li>
        ))}
      </ul>
    )
  }
  if (availabilityNote) {
    return (
      <div className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] text-ink-2">
        <Icon name="clock" size={12} className="text-ink-3" /> {availabilityNote}
      </div>
    )
  }
  return (
    <p className="mt-1.5 text-[12px] text-ink-3">
      Contact the hospital for availability.
    </p>
  )
}
